// Online cart: keep name edits in place; quantity changes still use the existing
// reservation job and page reload so checkout fields/payment methods stay current.
export function initializeCartItems({ reloadPage = () => window.location.reload() } = {}) {
  const root = document.getElementById("cart_line_items");
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = "true";
  root.classList.add("online-cart--enhanced");

  const form = document.getElementById("cart-order-form");
  initializeCartOrderForm();
  const submit = form?.querySelector('[type="submit"]');
  const content = root.querySelector(".online-cart__items");
  const status = root.querySelector(".online-cart__status");
  const error = root.querySelector(".online-cart__error");
  const writes = new Map();
  let saving = false;
  let activeWrite = null;
  let loading = false;
  let failed = null;
  let timer;
  let pollTimer;
  let reloadAfterSave = false;
  let submitRequested = false;
  let ready = false;
  let pending = false;
  let totalQuantity = 0;

  function syncState() {
    const busy = saving || loading || writes.size > 0;
    const savingQuantity = activeWrite?.quantity !== undefined;
    const blockQuantity = !ready || loading || savingQuantity || failed !== null || pending || reloadAfterSave;
    root.setAttribute("aria-busy", String(busy));
    status.textContent = saving ? root.dataset.savingLabel : "";
    const savingPerformance = activeWrite && root.querySelector(`.cart-ticket-line[data-line-item-id="${activeWrite.li_id}"]`)?.closest(".cart-performance");
    root.querySelectorAll(".cart-performance").forEach(performance => {
      performance.classList.toggle("is-saving", saving && performance === savingPerformance);
    });
    if (submit) submit.disabled = !ready || busy || failed !== null || pending || totalQuantity === 0;
    root.querySelectorAll(".cart-ticket-line").forEach(line => {
      const select = line.querySelector(".quantity-select");
      if (select) {
        select.disabled = blockQuantity;
        line.querySelector(".quantity-stepper__value").textContent = select.value;
        line.querySelector(".cart-quantity-decrease").disabled = blockQuantity || select.selectedIndex <= 0;
        line.querySelector(".cart-quantity-increase").disabled = blockQuantity || select.selectedIndex >= select.options.length - 1;
      }
      const remove = line.querySelector(".cart-ticket-remove");
      if (remove) {
        if (remove.dataset.locked === undefined) remove.dataset.locked = String(remove.disabled);
        remove.disabled = loading || savingQuantity || failed !== null || reloadAfterSave || remove.dataset.locked === "true";
      }
    });
    root.querySelectorAll(".cart-ticket-holder-name, .cart-single-tickets").forEach(input => {
      input.disabled = loading || reloadAfterSave || savingQuantity;
    });
  }

  function showError(message, retry) {
    failed = retry;
    error.hidden = false;
    error.querySelector("span").textContent = message;
    syncState();
  }

  function clearError() {
    failed = null;
    error.hidden = true;
  }

  async function request(url, data) {
    const response = await fetch(url, data ? {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: new URLSearchParams({ ...data, l: root.dataset.locale, source: "online_cart" })
    } : { credentials: "same-origin", headers: { "X-Requested-With": "XMLHttpRequest" } });
    if (!response.ok) throw new Error("Cart request failed");
    return response;
  }

  function readContentState() {
    const state = content.querySelector(".online-cart__content");
    if (!state) throw new Error("Missing cart content");
    ready = true;
    pending = state.dataset.pending === "true";
    totalQuantity = Number(state.dataset.totalQuantity);
    loading = false;
    clearError();
    window.updateCartTitle?.(pending);
    updatePaymentAvailability();
    syncState();
    if (state.dataset.empty === "true" || (!pending && root.dataset.initialPending === "true")) {
      reloadPage();
      return;
    }
    if (pending) pollTimer = setTimeout(() => refresh(), Number(state.dataset.pollDelay) || 1000);
  }

  async function refresh(remaining = 5, delay = 1000) {
    if (loading || saving || writes.size) return;
    clearTimeout(pollTimer);
    loading = true;
    syncState();
    try {
      const response = await request(root.dataset.itemsUrl);
      content.innerHTML = await response.text();
      readContentState();
    } catch (_) {
      loading = false;
      if (remaining > 1) {
        pollTimer = setTimeout(() => refresh(remaining - 1, Math.min(delay * 1.75, 30000)), delay);
      } else {
        showError(root.dataset.loadError, () => refresh());
      }
      syncState();
    }
  }

  async function flush() {
    clearTimeout(timer);
    if (saving || loading || failed) return;
    if (!writes.size) return;
    saving = true;
    syncState();
    while (writes.size) {
      // Save names and ticket options before a quantity job can delete their position.
      const [key, data] = Array.from(writes).find(([, write]) => write.quantity === undefined)
        || writes.entries().next().value;
      activeWrite = data;
      syncState();
      try {
        await request(root.dataset.updateUrl, data);
        if (writes.get(key) === data) writes.delete(key);
        if (data.quantity !== undefined) reloadAfterSave = true;
      } catch (_) {
        saving = false;
        activeWrite = null;
        showError(root.dataset.updateError, flush);
        return;
      }
    }
    saving = false;
    activeWrite = null;
    syncState();
    if (reloadAfterSave) {
      reloadPage();
    } else if (submitRequested) {
      submitRequested = false;
      form.requestSubmit(submit);
    }
  }

  function queue(key, data, delay = 500) {
    writes.set(key, data);
    clearTimeout(timer);
    if (!failed) timer = setTimeout(flush, delay);
    syncState();
  }

  function changeQuantity(line, offset) {
    const select = line.querySelector(".quantity-select");
    if (!select || select.disabled) return;
    const next = select.selectedIndex + offset;
    if (next < 0 || next >= select.options.length) return;
    select.selectedIndex = next;
    queue(`quantity-${line.dataset.lineItemId}`, { li_id: line.dataset.lineItemId, quantity: select.value });
  }

  function updateNameValidation(input, reveal = false) {
    if (reveal) input.dataset.validationShown = "true";
    if (input.dataset.validationShown === "true") {
      input.setAttribute("aria-invalid", String(!input.validity.valid));
    }
  }

  root.addEventListener("focusout", event => {
    if (event.target.matches(".cart-ticket-holder-name")) updateNameValidation(event.target, true);
  });

  // These fields belong to the order form but sit outside it in the DOM.
  // Capture native validation here, including checkout via Enter/requestSubmit.
  root.addEventListener("invalid", event => {
    if (event.target.matches(".cart-ticket-holder-name")) updateNameValidation(event.target, true);
  }, true);

  root.addEventListener("click", event => {
    const button = event.target.closest("button");
    if (!button || button.disabled) return;
    if (button.matches(".online-cart__retry")) {
      const retry = failed;
      clearError();
      retry?.();
      return;
    }
    const line = button.closest(".cart-ticket-line");
    if (!line) return;
    if (button.matches(".cart-quantity-decrease")) changeQuantity(line, -1);
    if (button.matches(".cart-quantity-increase")) changeQuantity(line, 1);
    if (button.matches(".cart-ticket-remove")) {
      if (button.dataset.confirmMessage && !window.confirm(button.dataset.confirmMessage)) return;
      queue(`quantity-${line.dataset.lineItemId}`, { li_id: line.dataset.lineItemId, quantity: 0 }, 0);
    }
  });

  root.addEventListener("input", event => {
    const input = event.target;
    if (!input.matches(".cart-ticket-holder-name")) return;
    updateNameValidation(input);
    const id = input.closest(".cart-ticket-line").dataset.lineItemId;
    queue(`name-${id}-${input.dataset.idx}`, { li_id: id, name: input.value, a: input.dataset.idx });
  });

  root.addEventListener("change", event => {
    const input = event.target;
    const line = input.closest(".cart-ticket-line");
    if (!line) return;
    const id = line.dataset.lineItemId;
    if (input.matches(".cart-single-tickets")) queue(`single-${id}`, { li_id: id, deliver_as_single_tickets: input.checked }, 0);
    if (input.matches(".quantity-select")) queue(`quantity-${id}`, { li_id: id, quantity: input.value });
  });

  form?.addEventListener("submit", event => {
    if (!ready || saving || loading || writes.size || failed || pending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitRequested = true;
      flush();
    }
  }, true);

  function updatePaymentAvailability() {
    const state = content.querySelector(".online-cart__content");
    const paypal = document.getElementById("order_payment_provider_paypal");
    if (!state || !paypal) return;
    const allowed = Number(state.dataset.totalCents) >= Number(state.dataset.paypalMinCents);
    const wasSelected = paypal.checked;
    paypal.disabled = !allowed;
    if (!allowed) paypal.checked = false;
    const label = paypal.closest("label");
    label.hidden = !allowed;
    label.classList.toggle("disabled", !allowed);
    if (!allowed && wasSelected) form?.querySelector('.payment_provider_change:not(:disabled)')?.click();
  }

  window.updateCartPaymentAvailability = updatePaymentAvailability;
  if (content.querySelector(".online-cart__content")) {
    readContentState();
  } else {
    refresh();
  }
}

// Online order fields: sync dynamic dates and reveal native validation.
export function initializeCartOrderForm() {
  const form = document.getElementById("cart-order-form");
  if (!form || form.classList.contains("cart-order--enhanced")) return;

  function syncDate(group) {
    const parts = ["day", "month", "year"].map(part => group.querySelector(`.date-input-${part}`).value);
    group.querySelector(".date-input-res").value = parts.every(Boolean) ? parts.join(".") : "";
  }

  function updateValidation(input, reveal = false) {
    if (!input.matches("input, select, textarea") || !input.willValidate) return;
    const fields = input.type === "radio"
      ? Array.from(form.elements).filter(field => field.type === "radio" && field.name === input.name)
      : [input];
    fields.forEach(field => {
      if (reveal || field.hasAttribute("aria-invalid")) {
        field.setAttribute("aria-invalid", String(!field.validity.valid || !!field.closest(".control-group.error")));
      }
    });
  }

  form.addEventListener("focusout", event => updateValidation(event.target, true));
  form.addEventListener("invalid", event => updateValidation(event.target, true), true);
  form.addEventListener("input", event => updateValidation(event.target));
  form.addEventListener("change", event => {
    if (event.target.matches(".date-input-comp")) syncDate(event.target.closest(".date-input-group"));
    updateValidation(event.target);
  });
  // Also capture restored selections that did not emit a change event.
  form.addEventListener("submit", () => form.querySelectorAll(".date-input-group").forEach(syncDate));
  form.classList.add("cart-order--enhanced");
}

window.initializeCartItems = initializeCartItems;
